import React from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Paper,
  Divider,
} from "@mui/material";
import { Add, CloudUpload } from "@mui/icons-material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PillBadge from "./PillBadge";
import { createBlobInContainer } from "src/utils/azureBlob";

export default function PromoCarousel({ config, onChange, miniGamesData }) {
  const updateSlides = (newSlides) => {
    onChange({ ...config, slides: newSlides });
  };
  const moveSlide = (slideId, direction) => {
    const slides = [...config.slides];
    const currentIndex = config.slides.findIndex(
      (slide) => (slide?.id || slide?.widgetPromoCarouselSlidesId) === slideId
    );
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < slides.length) {
      [slides[currentIndex], slides[newIndex]] = [
        slides[newIndex],
        slides[currentIndex],
      ];
      const updatedSlides = slides.map((slide, index) => ({
        ...slide,
        slideOrderId: index + 1,
      }));

      updateSlides(updatedSlides);
    }
  };

  // const updateOrder = (value) => {
  //   onChange({ ...config, order: value });
  // };

  const updateAutoRotate = (value) => {
    onChange({ ...config, autoRotate: value });
  };

  const updateLapseTime = (value) => {
    onChange({ ...config, lapseTime: value });
  };

  const addNewSlide = () => {
    const newSlide = {
      id: Date.now().toString(),
      title: "",
      linkType: "externalUrl",
      externalUrl: null,
      appPage: null,
      miniGames: null,
      slideOrderId:
        config.slides.length > 0
          ? Math.max(...config.slides.map((s) => s.slideOrderId)) + 1
          : 1,
    };
    updateSlides([...config.slides, newSlide]);
  };

  // Update slide properties
  const updateSlideProperty = (slideId, property, value) => {
    const updatedSlides = config.slides.map((slide) => {
      const slideIdentifier = slide?.id || slide?.widgetPromoCarouselSlidesId;
      if (slideIdentifier === slideId) {
        const updatedSlide = {
          ...slide,
          [property]: value,
        };

        // Reset dependent fields when 'link' changes
        if (property === "linkType") {
          updatedSlide.miniGames = null;
          updatedSlide.appPage = null;
          updatedSlide.externalUrl = null;
        }

        return updatedSlide;
      }

      return slide;
    });
    updateSlides(updatedSlides);
  };

  const isMoveUpDisabled = (slideId) => {
    const currentIndex = config.slides.findIndex(
      (slide) => (slide?.id || slide?.widgetPromoCarouselSlidesId) === slideId
    );
    return currentIndex === 0;
  };

  const isMoveDownDisabled = (slideId) => {
    const currentIndex = config.slides.findIndex(
      (slide) => (slide?.id || slide?.widgetPromoCarouselSlidesId) === slideId
    );
    return currentIndex === config.slides.length - 1;
  };

  const sortedSlides = [...config.slides].sort(
    (a, b) => a.slideOrderId - b.slideOrderId
  );

  const uploadFiles = (event, slideId, property) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      const imageType = event.target.dataset.imageType;
      if (!file || !imageType) return;
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!validTypes.includes(file.type)) {
        alert("Only JPG, JPEG, PNG, WEBP, and GIF formats are allowed.");
        event.target.value = null;
        return;
      }

      if (file.size > maxSize) {
        alert("File size should not exceed 2MB.");
        event.target.value = null;
        return;
      }

      const newFileName = file.name.replace(/\s+/g, "_");
      const renamedFile = new File([file], newFileName, {
        type: file.type,
      });

      const uploadFilesAsync = async () => {
        try {
          await createBlobInContainer(renamedFile);
          const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
          const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
          const imageUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${renamedFile.name}`;

          updateSlideProperty(slideId, property, imageUrl);
        } catch (err) {
          console.error(err);
        } finally {
          event.target.value = null;
        }
      };

      uploadFilesAsync();
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Promos
        </Typography>

        <Box display="flex" gap={3} flexWrap="wrap">
          {/* <FormControl size="small" style={{ minWidth: 120 }}>
            <InputLabel>Order</InputLabel>
            <Select
              value={config.order}
              label="Order"
              onChange={(e) => updateOrder(e.target.value)}
            >
              <MenuItem value="random">Random</MenuItem>
              <MenuItem value="sequential">Sequential</MenuItem>
            </Select>
          </FormControl> */}

          <FormControl size="small" style={{ minWidth: 100 }}>
            <InputLabel>Auto Rotate</InputLabel>
            <Select
              value={config.autoRotate}
              label="Auto Rotate"
              onChange={(e) => updateAutoRotate(e.target.value)}
            >
              <MenuItem value={true}>Yes</MenuItem>
              <MenuItem value={false}>No</MenuItem>
            </Select>
          </FormControl>
          {config?.autoRotate ? (
            <FormControl size="small" style={{ minWidth: 100 }}>
              <InputLabel>Lapse Time</InputLabel>
              <Select
                value={config.lapseTime}
                label="Lapse Time"
                onChange={(e) => updateLapseTime(e.target.value)}
              >
                <MenuItem value="3">3s</MenuItem>
                <MenuItem value="5">5s</MenuItem>
                <MenuItem value="10">10s</MenuItem>
              </Select>
            </FormControl>
          ) : (
            ""
          )}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Slides
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {sortedSlides?.map((slide, index) => {
          return (
            <Paper key={slide.id} sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <PillBadge slideOrderId={slide.slideOrderId} />
                <TextField
                  label="Title (Internal)"
                  size="small"
                  variant="standard"
                  value={slide.title}
                  onChange={(e) =>
                    updateSlideProperty(
                      slide.id || slide?.widgetPromoCarouselSlidesId,
                      "title",
                      e.target.value
                    )
                  }
                />
                <FormControl size="small">
                  <InputLabel>Link</InputLabel>
                  <Select
                    value={slide.linkType}
                    label="Link"
                    onChange={(e) =>
                      updateSlideProperty(
                        slide.id || slide?.widgetPromoCarouselSlidesId,
                        "linkType",
                        e.target.value
                      )
                    }
                  >
                    <MenuItem value="externalUrl">External Url</MenuItem>
                    <MenuItem value="appPage">App Page</MenuItem>
                    <MenuItem value="miniGames">Mini-Games</MenuItem>
                  </Select>
                </FormControl>
                {slide.linkType === "externalUrl" && (
                  <TextField
                    variant="standard"
                    size="small"
                    value={slide.externalUrl}
                    onChange={(e) =>
                      updateSlideProperty(
                        slide.id || slide?.widgetPromoCarouselSlidesId,
                        "externalUrl",
                        e.target.value
                      )
                    }
                    label="Url Link"
                  />
                )}
                {slide.linkType === "appPage" && (
                  <FormControl size="small">
                    <InputLabel>App Page</InputLabel>
                    <Select
                      style={{ minWidth: 200 }}
                      value={slide.appPage}
                      label="App Page"
                      onChange={(e) =>
                        updateSlideProperty(
                          slide.id || slide?.widgetPromoCarouselSlidesId,
                          "appPage",
                          e.target.value
                        )
                      }
                    >
                      <MenuItem value="account">Account</MenuItem>
                      <MenuItem value="prizeDraw">Prize Draw</MenuItem>
                      <MenuItem value="eplUf">EPL UF</MenuItem>
                      <MenuItem value="coins">Coins</MenuItem>
                      <MenuItem value="packs">Packs</MenuItem>
                    </Select>
                  </FormControl>
                )}
                {slide.linkType === "miniGames" && (
                  <FormControl size="small">
                    <InputLabel>Mini-Games</InputLabel>
                    <Select
                      style={{ minWidth: 200 }}
                      value={slide.miniGames}
                      label="Mini-Games"
                      disabled={!miniGamesData.length}
                      onChange={(e) =>
                        updateSlideProperty(
                          slide.id || slide?.widgetPromoCarouselSlidesId,
                          "miniGames",
                          e.target.value
                        )
                      }
                    >
                      {miniGamesData?.map((game, index) => (
                        <MenuItem key={index} value={game.id}>
                          {game.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>

              <Box display="flex" gap={2} flexWrap="wrap"></Box>
              {slide.imageUrl ? (
                <img
                  src={slide.imageUrl}
                  alt={`slide ${slide.id}`}
                  width={150}
                  height={150}
                  style={{ marginTop: 2 }}
                />
              ) : (
                <Box
                  sx={{
                    marginTop: 2,
                    width: 150,
                    height: 150,
                    borderRadius: 1,
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    gap: 2,
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CloudUpload sx={{ fontSize: 40, color: "#ccc" }} />
                  <span>334x80</span>
                </Box>
              )}
              <input
                accept="image/*"
                style={{ display: "none" }}
                id={`upload-${slide?.id || slide?.widgetPromoCarouselSlidesId}`}
                type="file"
                hidden
                data-image-type={index}
                onChange={(e) =>
                  uploadFiles(
                    e,
                    slide.id || slide.widgetPromoCarouselSlidesId,
                    "imageUrl"
                  )
                }
              />
              <label
                htmlFor={`upload-${
                  slide?.id || slide?.widgetPromoCarouselSlidesId
                }`}
              >
                <Button
                  variant="outlined"
                  component="span"
                  size="small"
                  sx={{ mt: 2 }}
                >
                  Upload Image
                </Button>
              </label>

              <Box
                display="flex"
                flexDirection="column"
                alignItems="end"
                justifyContent="flex-end"
                mt={1}
              >
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  sx={{ fontSize: "0.75rem" }}
                  onClick={() =>
                    moveSlide(
                      slide.id || slide?.widgetPromoCarouselSlidesId,
                      "up"
                    )
                  }
                  disabled={isMoveUpDisabled(
                    slide.id || slide?.widgetPromoCarouselSlidesId
                  )}
                >
                  Move{" "}
                  <ArrowUpwardIcon
                    fontSize="small"
                    sx={{ ml: 0.5, color: "#3167ca" }}
                  />
                </Button>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  sx={{ fontSize: "0.75rem" }}
                  onClick={() =>
                    moveSlide(
                      slide.id || slide?.widgetPromoCarouselSlidesId,
                      "down"
                    )
                  }
                  disabled={isMoveDownDisabled(
                    slide.id || slide?.widgetPromoCarouselSlidesId
                  )}
                >
                  Move{" "}
                  <ArrowDownwardIcon
                    fontSize="small"
                    sx={{ ml: 0.5, color: "#3167ca" }}
                  />
                </Button>
              </Box>
            </Paper>
          );
        })}
        <Divider sx={{ mb: 2 }} />
        <Button
          variant="text"
          size="small"
          onClick={addNewSlide}
          sx={{ color: "primary.main", mt: 2 }}
          startIcon={<Add fontSize="small" />}
        >
          Add New Slide
        </Button>
      </Box>
    </Box>
  );
}
